import { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import { FiSearch, FiUser, FiMail, FiPhone, FiBook, FiCalendar, FiAward, FiMessageSquare } from "react-icons/fi";
import ViewStudents from "../Admin/Student/ViewStudents";
import { getFileUrl } from "../../utils/fileUrl";

const Student = ({ onMessageParent }) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("view");
  const [data, setData] = useState({
    enrollmentNo: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    semester: "",
    branch: "",
    gender: "",
    profile: "",
    certifications: [],
  });
  const [id, setId] = useState("");
  const [marks, setMarks] = useState(null);

  const searchStudentHandler = (e) => {
    e.preventDefault();
    setId("");
    setData({
      enrollmentNo: "",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      semester: "",
      branch: "",
      gender: "",
      profile: "",
      certifications: [],
    });
    setMarks(null);

    toast.loading("Getting Student...");
    const headers = { "Content-Type": "application/json" };

    axios
      .post(`${baseApiURL()}/student/details/getDetails`, { enrollmentNo: search }, { headers })
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          const user = response.data.user[0];
          if (!user) {
            toast.error("No Student Found!");
          } else {
            toast.success(response.data.message);
            setData({
              enrollmentNo: user.enrollmentNo,
              firstName: user.firstName,
              middleName: user.middleName,
              lastName: user.lastName,
              email: user.email,
              phoneNumber: user.phoneNumber,
              semester: user.semester,
              branch: user.branch,
              gender: user.gender,
              profile: user.profile,
              certifications: user.certifications || [],
            });
            setId(user._id);
            fetchMarks(user.enrollmentNo);
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error.response?.data?.message || "Error occurred");
        console.error(error);
      });
  };

  const fetchMarks = (enrollmentNo) => {
    axios
      .post(`${baseApiURL()}/marks/getMarks`, { enrollmentNo })
      .then((response) => {
        if (response.data.success) {
          setMarks(response.data.Mark[0]);
        } else {
          toast.error("Marks not available");
        }
      })
      .catch((error) => {
        toast.error("Error fetching marks");
        console.error(error);
      });
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Student Management</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Search and view student academic information</p>
        </div>
      </div>

      {/* Sub-tab Navigation */}
      <div className="bento-card p-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
        <div className="grid grid-cols-2 gap-2">
          <button
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
              selected === "view"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => setSelected("view")}
          >
            <div className="flex items-center justify-center space-x-2">
              <FiUser className="w-4 h-4" />
              <span>View All Students</span>
            </div>
          </button>

          <button
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
              selected === "search"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => setSelected("search")}
          >
            <div className="flex items-center justify-center space-x-2">
              <FiSearch className="w-4 h-4" />
              <span>Search Student</span>
            </div>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="w-full">
            {selected === "search" && (
              <div className="space-y-6">
                {/* Search Section */}
                <div className="max-w-2xl mx-auto">
                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3">Search Student</h2>
                    <form onSubmit={searchStudentHandler} className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                          Enrollment Number
                        </label>
                        <div className="relative">
                          <FiSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Enter enrollment number..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-mono font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                            required
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-xs shadow-xs transition cursor-pointer"
                      >
                        Search Student
                      </button>
                    </form>
                  </div>
                </div>

                {/* Student Details */}
                {id && (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-base font-bold text-white">
                          Student Information
                        </h3>
                        {onMessageParent && (
                          <button
                            onClick={() => onMessageParent(data.enrollmentNo)}
                            className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-3 py-1.5 rounded-xl flex items-center gap-1.5 text-xs shadow-xs transition cursor-pointer"
                          >
                            <FiMessageSquare /> Message Parent
                          </button>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500">Full Name</label>
                              <p className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                                {data.firstName} {data.middleName} {data.lastName}
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500">Enrollment No</label>
                              <p className="text-base font-bold font-mono text-indigo-600 dark:text-indigo-400 mt-0.5">{data.enrollmentNo}</p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500">Email</label>
                              <div className="flex items-center space-x-2 mt-0.5">
                                <FiMail className="text-slate-400" />
                                <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{data.email}</p>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500">Phone</label>
                              <div className="flex items-center space-x-2 mt-0.5">
                                <FiPhone className="text-slate-400" />
                                <p className="text-xs font-medium text-slate-800 dark:text-slate-200">+91 {data.phoneNumber}</p>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500">Branch</label>
                              <div className="flex items-center space-x-2 mt-0.5">
                                <FiBook className="text-slate-400" />
                                <p className="text-xs font-bold text-slate-800 dark:text-slate-200">{data.branch}</p>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500">Semester</label>
                              <div className="flex items-center space-x-2 mt-0.5">
                                <FiCalendar className="text-slate-400" />
                                <span className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-bold border border-indigo-100 dark:border-indigo-800">
                                  Semester {data.semester}
                                </span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-slate-400 dark:text-slate-500 mb-1">Gender</label>
                              <span className={`px-2.5 py-0.5 rounded-lg text-xs font-bold ${
                                data.gender === 'Male' 
                                  ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800' 
                                  : 'bg-pink-50 dark:bg-pink-950/60 text-pink-700 dark:text-pink-300 border border-pink-100 dark:border-pink-800'
                              }`}>
                                {data.gender}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Certifications Section */}
                        <div className="mt-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                          <div className="flex items-center space-x-2 mb-3">
                            <FiAward className="text-indigo-600 dark:text-indigo-400 text-base" />
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Certifications</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {data.certifications.length > 0 ? (
                              data.certifications.map((cert, index) => (
                                <a
                                  key={index}
                                  href={getFileUrl(cert)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2 p-3 bg-slate-50 dark:bg-slate-950 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition border border-slate-200 dark:border-slate-800"
                                >
                                  <FiAward className="text-indigo-600 dark:text-indigo-400" />
                                  <span className="text-indigo-700 dark:text-indigo-300 font-bold text-xs">Certification {index + 1}</span>
                                </a>
                              ))
                            ) : (
                              <p className="text-xs text-slate-400 col-span-full">No certifications available</p>
                            )}
                          </div>
                        </div>

                        {/* Marks Section */}
                        <div className="mt-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-3">Academic Marks</h4>
                          {marks ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-slate-50 dark:bg-slate-950 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                <h5 className="font-bold text-xs text-slate-900 dark:text-white mb-2">Internal Marks</h5>
                                <pre className="text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto font-mono">
                                  {JSON.stringify(marks.internal, null, 2)}
                                </pre>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-950 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                                <h5 className="font-bold text-xs text-slate-900 dark:text-white mb-2">External Marks</h5>
                                <pre className="text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto font-mono">
                                  {JSON.stringify(marks.external, null, 2)}
                                </pre>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400">No marks available</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selected === "view" && <ViewStudents onMessageParent={onMessageParent} />}
      </div>
    </div>
  );
};

export default Student;
